import { JWT_ACCESS_TOKEN } from '~/constants/auth'
import { logError } from '~/utils/log-error'
import type { TTickerData } from '~/server/types/ticker'
import type { TResponseHttp } from '~/server/types/http'
import { $fetch } from 'ofetch'
import { useAsyncData, useCookie } from '#app'

export function useTicker() {
  const jwtToken = useCookie(JWT_ACCESS_TOKEN)
  const isAuthenticated = computed(() => !!jwtToken.value)
  const shouldRefetch = ref(false)

  const {
    data: indexes,
    status,
    refresh,
    error,
  } = useAsyncData<TTickerData>(
    'ticker-data',
    async () => {
      const authorization = isAuthenticated.value
        ? `Bearer ${jwtToken.value}`
        : ''
      const response = await $fetch<TResponseHttp<TTickerData>>('/api/ticker', {
        method: 'POST',
        headers: {
          Authorization: authorization,
        },
      })

      if (response.success && response.data) {
        return response.data.map((item) => {
          return {
            name: item.name,
            values: {
              ...item.values,
              hasAccess: isAuthenticated.value || item.values.hasAccess,
            },
          }
        })
      }
      return []
    },
    {
      lazy: true,
      server: true,
      immediate: true,
      default: () => [],
    },
  )

  const refreshTickerData = async (forceRefresh = false) => {
    if (!forceRefresh && import.meta.client) {
      return
    }

    try {
      shouldRefetch.value = true
      await refresh()
    } catch (error) {
      logError(error)
    } finally {
      shouldRefetch.value = false
    }
  }

  watch(
    isAuthenticated,
    async () => {
      if (import.meta.client) {
        await refreshTickerData(true)
      }
    },
    { immediate: true },
  )

  return {
    indexes,
    isAuthenticated,
    loading: computed(
      () => status.value === 'pending' || status.value === 'idle',
    ),
    error,
    refreshTickerData,
  }
}
